
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Key } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface PasswordHistoryEntry {
  id: string;
  changed_at: string;
  method: string;
}

interface PasswordManagementProps {
  passwordHistory: PasswordHistoryEntry[];
  isLoadingHistory: boolean;
  changePassword: (args: { currentPassword: string; newPassword: string; }) => void;
  isChangingPassword: boolean;
}

const PasswordManagement = ({ passwordHistory, isLoadingHistory, changePassword, isChangingPassword }: PasswordManagementProps) => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    changePassword({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          Password Security
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                />
              </div>
              <Button type="submit" disabled={isChangingPassword}>
                {isChangingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </div>

          <div>
            <h4 className="font-medium mb-3">Password History</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {isLoadingHistory ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : passwordHistory.length > 0 ? (
                passwordHistory.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                    <span>{format(new Date(entry.changed_at), 'MMM dd, yyyy HH:mm')}</span>
                    <Badge variant="outline">{entry.method}</Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No password changes recorded</p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PasswordManagement;
